import { useParams, useNavigate } from 'react-router-dom';
import { config } from "../../data/config";
import { getCookies } from "cookies-next";
import axios from "axios";
import { useEffect, useState } from "react";
import BreadCrump from "../tools/breadcrump";
import HeaderOfPage from "../tools/headerOfPage";
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { IM } from 'country-flag-icons/react/3x2';
import ImageUploader from '../tools/uploaderImage';
const inputstyle = "py-2.5 px-3 block w-full border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:pointer-events-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-all";

const AGENT_LANGUAGES = [
    { value: 'english', label: 'English' },
    { value: 'persian', label: 'Persian' },
    { value: 'arabic', label: 'Arabic' },
    { value: 'chinese', label: 'Chinese' },
    { value: 'russian', label: 'Russian' },
    { value: 'spanish', label: 'Spanish' },
    { value: 'japanese', label: 'Japanese' },
    { value: 'turkish', label: 'Turkish' },
    { value: 'kurdish', label: 'Kurdish' },
    { value: 'french', label: 'French' },
    { value: 'german', label: 'German' },
    { value: 'italian', label: 'Italian' },
    { value: 'portuguese', label: 'Portuguese' },
   
] as const;

export default function EditAgent() {
    const { t } = useTranslation();
    const { Id } = useParams();
    const navigate = useNavigate();
    const cookie = getCookies();
    const isNew = Id === 'new' || Id === undefined;
    const invalidId = !isNew && (Id === 'undefined' || !Id);

    const [data, setData] = useState<{
        id?: string;
        name: string;
        lastname: string;
        phone: string;
        telegram: string;
        whatsapp: string;
        email: string;
        languages: string[];
        active: boolean;
        arranged: number;
    }>({
        name: '',
        lastname: '',
        phone: '',
        telegram: '',
        whatsapp: '',
        email: '',
        languages: [],
        active: true,
        arranged: 1
    });
    const [loading, setLoading] = useState(!isNew && !invalidId);

    const breadcrump = [
        { name: t('Dashboard'), url: '/dashboard' },
        { name: t('Real Estate System'), url: '/dashboard/RealEstate' },
        { name: t('Agent Management'), url: '/dashboard/RealEstate/agents' },
        { name: isNew ? t('Add Agent') : t('Edit Agent'), url: null },
    ];

    const load = async () => {
        if (isNew || invalidId) return;
        try {
            const res = await axios({
                method: 'get',
                url: `${config.url}/v1/dashboard/realestate/agents/${Id}`,
                headers: { Authorization: 'Bearer ' + cookie['token'] }
            });
            const msg = res.data.message || res.data;
            setData({
                id: msg._id ?? undefined,
                name: msg.name ?? '',
                lastname: msg.lastname ?? '',
                phone: msg.phone ?? '',
                telegram: msg.telegram ?? '',
                whatsapp: msg.whatsapp ?? '',
                email: msg.email ?? '',
                languages: Array.isArray(msg.languages) ? msg.languages : [],
                active: msg.active !== false,
                arranged: msg.arranged != null ? Number(msg.arranged) : 1
            });
        } catch (_) {
            Swal.fire(t("Error"), t("Could not load agent."), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [Id]);

    const toggleLanguage = (value: string) => {
        setData(prev => {
            const next = prev.languages.includes(value)
                ? prev.languages.filter(l => l !== value)
                : [...prev.languages, value];
            return { ...prev, languages: next };
        });
    };

    const save = async () => {
        const token = cookie['token'] || cookie.token;
        if (!token) {
            Swal.fire(t("Error"), t("Please log in again."), "error");
            return;
        }
        const nameTrim = (data.name || '').trim();
        if (!nameTrim) {
            Swal.fire(t("Validation"), t("Name is required."), "warning");
            return;
        }

        const body = {
            name: data.name.trim(),
            lastname: data.lastname.trim(),
            phone: data.phone.trim(),
            telegram: data.telegram.trim(),
            whatsapp: data.whatsapp.trim(),
            email: data.email.trim(),
            languages: data.languages,
            active: data.active,
            arranged: Number(data.arranged) || 1
        };
        const headers = {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        };

        try {
            if (isNew) {
                await axios.post(config.url + '/v1/dashboard/realestate/agents', body, { headers });
                Swal.fire(t("Saved"), t("Agent created."), "success");
            } else {
                await axios.put(config.url + '/v1/dashboard/realestate/agents/' + Id, body, { headers });
                Swal.fire(t("Saved"), t("Agent updated."), "success");
            }
            navigate('/dashboard/RealEstate/agents');
        } catch (err: any) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message ?? err?.message ?? t("Could not save.");
            const fullMsg = status === 401
                ? t("Please log in again.")
                : status === 400 || status === 422
                    ? (typeof msg === 'string' ? msg : JSON.stringify(msg))
                    : err?.response?.data?.error ?? msg;
            Swal.fire(t("Error"), fullMsg, "error");
        }
    };

    if (loading) {
        return (
            <div className="text-left bg-gray-50 dark:bg-gray-900 p-3 m-4 directions min-h-[100vh] mb-5">
                <BreadCrump data={breadcrump} />
                <div className="flex items-center gap-3 py-12">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-500 dark:text-gray-400">{t("Loading...")}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="text-left bg-gray-50 dark:bg-gray-900 p-4 m-4 rounded-xl directions min-h-[100vh] mb-5">
            <BreadCrump data={breadcrump} />
            <HeaderOfPage name={isNew ? t("Add Agent") : t("Edit Agent")} />

            <div className="max-w-2xl space-y-6">
                <section className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 p-5 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-600 pb-2">
                        {t("Basic info")}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t("Name")} *</label>
                            <input
                                type="text"
                                value={data.name}
                                onChange={(e) => setData(prev => ({ ...prev, name: e.target.value }))}
                                className={inputstyle}
                                placeholder={t("Name")}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t("Last name")}</label>
                            <input
                                type="text"
                                value={data.lastname}
                                onChange={(e) => setData(prev => ({ ...prev, lastname: e.target.value }))}
                                className={inputstyle}
                                placeholder={t("Last name")}
                            />
                        </div>
                    </div>
                </section>

                <section className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 p-5 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-600 pb-2">
                        {t("Contact")}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t("Phone")}</label>
                            <input
                                type="text"
                                value={data.phone}
                                onChange={(e) => setData(prev => ({ ...prev, phone: e.target.value }))}
                                className={inputstyle}
                                placeholder={t("Phone number")}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t("Email")}</label>
                            <input
                                type="email"
                                value={data.email}
                                onChange={(e) => setData(prev => ({ ...prev, email: e.target.value }))}
                                className={inputstyle}
                                placeholder={t("email@example.com")}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t("Telegram")}</label>
                            <input
                                type="text"
                                value={data.telegram}
                                onChange={(e) => setData(prev => ({ ...prev, telegram: e.target.value }))}
                                className={inputstyle}
                                placeholder={t("@username or link")}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t("WhatsApp")}</label>
                            <input
                                type="text"
                                value={data.whatsapp}
                                onChange={(e) => setData(prev => ({ ...prev, whatsapp: e.target.value }))}
                                className={inputstyle}
                                placeholder={t("WhatsApp number")}
                            />
                        </div>
                    </div>
                </section>

                <section className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 p-5 space-y-4">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-600 pb-2 mb-1">
                            {t("Languages spoken fluently")}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("Select all that apply")}</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {AGENT_LANGUAGES.map(({ value, label }) => {
                            const isSelected = data.languages.includes(value);
                            return (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => toggleLanguage(value)}
                                    className={`
                                        py-3 px-4 rounded-lg text-sm font-medium text-left transition-all duration-200
                                        border-2 focus:outline-none focus:ring-2 focus:ring-offset-1 dark:focus:ring-offset-gray-800 focus:ring-blue-400
                                        ${isSelected
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-600 text-blue-800 dark:text-blue-200 shadow-sm'
                                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }
                                    `}
                                >
                                    <span className="flex items-center gap-2">
                                        <span className={`
                                            w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0
                                            ${isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700'}
                                        `}>
                                            {isSelected && (
                                                <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </span>
                                        {t(label)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {data.languages.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {data.languages.length} {t("selected")}
                        </p>
                    )}
                </section>

                <section className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 p-5 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-600 pb-2">
                        {t("Status & order")}
                    </h3>
                    <div className="flex flex-wrap items-center gap-6">
                        <label className="flex items-center gap-2.5 cursor-pointer group">
                            <input
                                type="checkbox"
                                checked={data.active}
                                onChange={(e) => setData(prev => ({ ...prev, active: e.target.checked }))}
                                className="w-4 h-4 rounded border-gray-300 dark:border-gray-500 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100">{t("Active")}</span>
                        </label>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{t("Display order")}</label>
                            <div className="flex items-stretch rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800 w-fit">
                                <button
                                    type="button"
                                    title={t("Move up (decrease number)")}
                                    onClick={() => setData(prev => ({ ...prev, arranged: Math.max(0, (prev.arranged ?? 1) - 1) }))}
                                    className="flex items-center justify-center w-10 border-r border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                                </button>
                                <input
                                    type="number"
                                    min={0}
                                    value={data.arranged}
                                    onChange={(e) => setData(prev => ({ ...prev, arranged: Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                                    className="w-16 py-2 px-2 text-center border-0 bg-transparent text-sm focus:ring-2 focus:ring-blue-500/20 focus:ring-inset dark:bg-transparent dark:text-gray-100"
                                />
                                <button
                                    type="button"
                                    title={t("Move down (increase number)")}
                                    onClick={() => setData(prev => ({ ...prev, arranged: (prev.arranged ?? 0) + 1 }))}
                                    className="flex items-center justify-center w-10 border-l border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t("Lower number = higher in list")}</p>
                        </div>
                    </div>
                </section>

                <section className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 p-5 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-600 pb-2">
                        {t("Profile image")}
                    </h3>
                    {data.id ? (
                        <ImageUploader id={data.id} component="Agent" />
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                            {t("Save the agent first to upload a profile image.")}
                        </p>
                    )}
                </section>

                <div className="flex items-center justify-end gap-3 pt-4 sticky bottom-0 py-3 -mx-1 px-1 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={() => navigate('/dashboard/RealEstate/agents')}
                        className="py-2.5 px-5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium transition-colors"
                    >
                        {t("Cancel")}
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        className="py-2.5 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm transition-colors focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                    >
                        {t("Save")}
                    </button>
                </div>
            </div>
        </div>
    );
}
